// Matches the extensions allowed by the "Upload Video" data type
const videoMimeTypes = {
  mp4: 'video/mp4',
  webm: 'video/webm',
} as const;

type VideoExtension = keyof typeof videoMimeTypes;
export type VideoMimeType = (typeof videoMimeTypes)[VideoExtension];

export type VideoMimeTypeResult =
  | { readonly found: true; readonly mimeType: VideoMimeType }
  | { readonly found: false };

const notFound: VideoMimeTypeResult = { found: false };

const isVideoExtension = (extension: string): extension is VideoExtension =>
  Object.prototype.hasOwnProperty.call(videoMimeTypes, extension);

/**
 * Gets the MIME type of a video file based on its URL.
 * @param url The URL of the video file.
 * @returns An object indicating whether the MIME type was found and the MIME type itself if found.
 */
export const getVideoMimeType = (url: string): VideoMimeTypeResult => {
  let pathname: string;
  try {
    pathname = new URL(url, window.location.href).pathname;
  } catch {
    return notFound;
  }

  const lastSegment = pathname.split('/').pop() ?? '';
  const dotIndex = lastSegment.lastIndexOf('.');
  if (dotIndex === -1) {
    return notFound;
  }

  const extension = lastSegment.slice(dotIndex + 1).toLowerCase();
  return isVideoExtension(extension)
    ? { found: true, mimeType: videoMimeTypes[extension] }
    : notFound;
};
